import React, { useMemo } from "react";
import {
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  CardMedia,
  Chip,
  Container,
  Divider,
  Grid,
  Stack,
  Typography,
} from "@mui/material";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Building2, FileText, ShieldCheck, Truck } from "lucide-react";

function B2BProductPage() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const id = Number(params.get("id") || 1);

  const brand = useMemo(
    () => ({
      accent: "#0EA5E9",
      accent2: "#22C55E",
      bg: "#F7FAFC",
      card: "#FFFFFF",
      text: "#0F172A",
      subtext: "rgba(15, 23, 42, 0.72)",
      stroke: "rgba(2, 6, 23, 0.10)",
    }),
    []
  );

  const product = useMemo(() => {
    const items = [
  {
    id: 1,
    title: "Safety Helmet (Bulk Pack)",
    category: "Industrial",
    price: 150,
    moq: 10,
    lead: "2-3 days",
    vendor: "SafeGear Industrial",
    image:
      "https://images.unsplash.com/photo-1581092921461-eab62e97a780?auto=format&fit=crop&w=800&q=80",
  },
  {
    id: 2,
    title: "Wi-Fi Router (Business)",
    category: "Electronics",
    price: 200,
    moq: 5,
    lead: "1-2 days",
    vendor: "NetWorks Pro",
    image:
      "https://images.unsplash.com/photo-1519389950473-47ba0277781c?auto=format&fit=crop&w=800&q=80",
  },
  {
    id: 3,
    title: "Packaging Tape (Case)",
    category: "Packaging",
    price: 80,
    moq: 24,
    lead: "Same day",
    vendor: "PackRight Supplies",
    image:
      "https://images.unsplash.com/photo-1586528116311-ad8dd3c8310d?auto=format&fit=crop&w=800&q=80",
  },
  {
    id: 4,
    title: "Ergonomic Chair (Lot)",
    category: "Office",
    price: 250,
    moq: 4,
    lead: "3-5 days",
    vendor: "OfficeWorks Pro",
    image:
      "https://images.unsplash.com/photo-1586023492125-27b2c045efd7?auto=format&fit=crop&w=800&q=80",
  },
];

    return items.find((x) => x.id === id) || items[0];
  }, [id]);

  return (
    <Box sx={{ minHeight: "100vh", bgcolor: brand.bg, py: 6 }}>
      <Container maxWidth="lg">
        <Stack direction={{ xs: "column", md: "row" }} spacing={1} justifyContent="space-between" alignItems={{ xs: "flex-start", md: "center" }} sx={{ mb: 2 }}>
          <Button
            onClick={() => navigate("/B2BShop")}
            sx={{ textTransform: "none", fontWeight: 900 }}
          >
            ← Back to catalog
          </Button>
          <Stack direction="row" spacing={1} sx={{ flexWrap: "wrap" }}>
            <Chip icon={<ShieldCheck size={16} />} label="B2B-ready invoice" />
            <Chip icon={<Truck size={16} />} label={`Lead: ${product.lead}`} />
          </Stack>
        </Stack>

        <Card sx={{ borderRadius: 4, border: `1px solid ${brand.stroke}` }}>
          <CardContent>
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <CardMedia
                  component="img"
                  image={product.image}
                  alt={product.title}
                  sx={{
                    borderRadius: 3,
                    height: { xs: 260, md: 420 },
                    objectFit: "cover",
                  }}
                />
                <Grid container spacing={1.5} sx={{ mt: 0.5 }}>
                  {[1, 2, 3].map((i) => (
                    <Grid item xs={4} key={i}>
                      <CardMedia
                        component="img"
                        image={product.image + `&sig=${i}`}
                        alt={`${product.title} ${i}`}
                        sx={{
                          borderRadius: 2.5,
                          height: 96,
                          objectFit: "cover",
                          border: `1px solid ${brand.stroke}`,
                        }}
                      />
                    </Grid>
                  ))}
                </Grid>
              </Grid>

              <Grid item xs={12} md={6}>
                <Typography variant="h4" sx={{ fontWeight: 950, color: brand.text }}>
                  {product.title}
                </Typography>
                <Typography variant="body2" sx={{ color: brand.subtext, mt: 0.5 }}>
                  {product.vendor} • Category: {product.category}
                </Typography>

                <Stack direction="row" spacing={1} sx={{ mt: 2, flexWrap: "wrap" }}>
                  <Chip label={`MOQ ${product.moq}`} />
                  <Chip label={`Lead: ${product.lead}`} />
                  <Chip label="GST invoice" />
                </Stack>

                <Divider sx={{ my: 2 }} />

                <Typography variant="h5" sx={{ fontWeight: 950, color: brand.text }}>
                  From ₹{product.price}
                </Typography>
                <Typography variant="body2" sx={{ color: brand.subtext, mt: 0.5 }}>
                  Bulk pricing tiers (demo):
                </Typography>

                <Box
                  sx={{
                    mt: 1.5,
                    borderRadius: 3,
                    border: `1px solid ${brand.stroke}`,
                    overflow: "hidden",
                  }}
                >
                  {[
                    { qty: `${product.moq}+`, price: `₹${product.price}` },
                    { qty: "50+", price: `₹${Math.max(1, Math.round(product.price * 0.94))}` },
                    { qty: "200+", price: `₹${Math.max(1, Math.round(product.price * 0.9))}` },
                  ].map((row, idx) => (
                    <Box
                      key={row.qty}
                      sx={{
                        px: 2,
                        py: 1.25,
                        display: "flex",
                        justifyContent: "space-between",
                        bgcolor: idx % 2 === 0 ? "white" : "rgba(2,6,23,0.02)",
                      }}
                    >
                      <Typography sx={{ fontWeight: 900, color: brand.text }}>
                        Qty {row.qty}
                      </Typography>
                      <Typography sx={{ fontWeight: 950, color: brand.text }}>
                        {row.price}
                      </Typography>
                    </Box>
                  ))}
                </Box>

                <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} sx={{ mt: 2.5 }}>
                  <Button
                    variant="contained"
                    onClick={() => navigate("/B2BCart")}
                    sx={{
                      borderRadius: 2.5,
                      textTransform: "none",
                      fontWeight: 950,
                      color: "white",
                      background: `linear-gradient(135deg, ${brand.accent} 0%, ${brand.accent2} 100%)`,
                    }}
                  >
                    Add to cart
                  </Button>
                  <Button
                    variant="outlined"
                    onClick={() => navigate("/B2BRFQ")}
                    sx={{
                      borderRadius: 2.5,
                      textTransform: "none",
                      fontWeight: 950,
                      borderColor: brand.stroke,
                      color: brand.text,
                    }}
                  >
                    Request a quote
                  </Button>
                </Stack>

                <Grid container spacing={2} sx={{ mt: 1 }}>
                  <Grid item xs={12} sm={6}>
                    <Card sx={{ borderRadius: 3, border: `1px solid ${brand.stroke}` }}>
                      <CardContent>
                        <Stack direction="row" spacing={1.5} alignItems="center">
                          <Box sx={{ width: 40, height: 40, borderRadius: 2, display: "grid", placeItems: "center", bgcolor: "rgba(14,165,233,0.10)" }}>
                            <Building2 size={18} />
                          </Box>
                          <Box sx={{ minWidth: 0 }}>
                            <Typography sx={{ fontWeight: 950, color: brand.text }} noWrap>
                              Vendor
                            </Typography>
                            <Typography variant="body2" sx={{ color: brand.subtext }} noWrap>
                              {product.vendor}
                            </Typography>
                          </Box>
                        </Stack>
                      </CardContent>
                    </Card>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Card sx={{ borderRadius: 3, border: `1px solid ${brand.stroke}` }}>
                      <CardContent>
                        <Stack direction="row" spacing={1.5} alignItems="center">
                          <Box sx={{ width: 40, height: 40, borderRadius: 2, display: "grid", placeItems: "center", bgcolor: "rgba(34,197,94,0.10)" }}>
                            <FileText size={18} />
                          </Box>
                          <Box sx={{ minWidth: 0 }}>
                            <Typography sx={{ fontWeight: 950, color: brand.text }} noWrap>
                              Documents
                            </Typography>
                            <Typography variant="body2" sx={{ color: brand.subtext }} noWrap>
                              Spec sheet • Warranty
                            </Typography>
                          </Box>
                        </Stack>
                      </CardContent>
                    </Card>
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      </Container>
    </Box>
  );
}

export default B2BProductPage;

